/**
 * Generato automaticamente.
 * procedimenti-authentication-manager
 */
import { useMutation } from 'react-query';
import type { MutationFunction, UseMutationOptions } from 'react-query';
import { apiClientRequest } from '@framework/api-client';
import type { ErrorType } from '@framework/api-client';
import type { LoginRequest, LoginResponse } from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * durata token 10 minuti, refresh token 120 minuti
 * @summary Effettua il login con username e password ed ottiene i token JWT (access & refresh) in cambio
 */
export const postLdapLogin = (
  loginRequest: LoginRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<LoginResponse>(
    {
      url: `/procedimenti-authentication-manager/ldap/login`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: loginRequest,
    },
    options,
  );
};

export const getPostLdapLoginMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postLdapLogin>>,
    TError,
    { data: LoginRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postLdapLogin>>,
  TError,
  { data: LoginRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postLdapLogin>>,
    { data: LoginRequest }
  > = props => {
    const { data } = props ?? {};

    return postLdapLogin(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostLdapLoginMutationResult = NonNullable<
  Awaited<ReturnType<typeof postLdapLogin>>
>;
export type PostLdapLoginMutationBody = LoginRequest;
export type PostLdapLoginMutationError = ErrorType<unknown>;

/**
 * @summary Effettua il login con username e password ed ottiene i token JWT (access & refresh) in cambio
 */
export const usePostLdapLogin = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postLdapLogin>>,
    TError,
    { data: LoginRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostLdapLoginMutationOptions(options);

  return useMutation(mutationOptions);
};
